/**
 * Past papers, one row per paper (bug fix 1, spec 4 §4): under session headings, newest first,
 * each paper's files as chips by role. A paper's ⋯ adds a file, renames it or deletes it (asked
 * twice); a file's ⋯ moves it to another paper, says what it is, or deletes it. A file in no
 * paper asks "Which paper is this for?". Files still uploading, or still being read, wait in
 * "Uploading". Every change answers with the new listing, which the screen keeps.
 */
import { useState, type ReactNode } from "react";
import type { Doc, PastPaper, PastPaperListing, PastPaperRole } from "../api/api";
import {
  ROLE_WORD,
  bySession,
  chips,
  hasScheme,
  paperOption,
  papersWithoutScheme,
} from "./pastPapers";

export type UploadFailure = { name: string; message: string; retry: () => void };

type Props = {
  listing: PastPaperListing;
  /** Past-paper files on their way: uploading, finalising, or not yet read. */
  uploading: ReactNode[];
  failures: UploadFailure[];
  onMove: (documentId: string, pastPaperId: string) => Promise<void>;
  onRole: (documentId: string, role: PastPaperRole) => Promise<void>;
  onRename: (pastPaperId: string, name: string) => Promise<void>;
  onDeletePaper: (pastPaperId: string) => Promise<void>;
  onDeleteFile: (documentId: string) => Promise<void>;
  onAddFile: (pastPaperId: string, role: PastPaperRole, file: File) => Promise<void>;
};

const ROLES: PastPaperRole[] = ["question_paper", "mark_scheme", "insert"];
const pp = (d: Doc) => `${d.page_count ?? 0} pp`;

function Menu({ label, children }: { label: string; children: ReactNode }) {
  return (
    <details className="row-menu">
      <summary className="button button--quiet row-menu__toggle" aria-label={label}>
        ⋯
      </summary>
      <div className="row-menu__panel">{children}</div>
    </details>
  );
}

/** A file's own actions: move it, say what it is, delete it. */
function FileActions({ doc, papers, props }: { doc: Doc; papers: PastPaper[]; props: Props }) {
  const [moving, setMoving] = useState(false);
  const others = papers.filter((p) => p.id !== doc.past_paper_id);
  return (
    <>
      {moving ? (
        <label className="row-menu__field">
          Move to
          <select
            aria-label={`Move ${doc.filename} to`}
            defaultValue=""
            onChange={(e) => e.target.value && void props.onMove(doc.id, e.target.value)}
          >
            <option value="" disabled>
              Choose a paper
            </option>
            {others.map((p) => (
              <option key={p.id} value={p.id}>
                {paperOption(p)}
              </option>
            ))}
            <option value="new">A new paper</option>
          </select>
        </label>
      ) : (
        <button type="button" onClick={() => setMoving(true)}>
          Move to another paper…
        </button>
      )}
      {ROLES.filter((r) => r !== (doc.role ?? "question_paper")).map((r) => (
        <button key={r} type="button" onClick={() => void props.onRole(doc.id, r)}>
          This is {r === "insert" ? "an insert" : `a ${ROLE_WORD[r].toLowerCase()}`}
        </button>
      ))}
      <button type="button" onClick={() => void props.onDeleteFile(doc.id)}>
        Delete this file
      </button>
    </>
  );
}

function PaperRow({ paper, props }: { paper: PastPaper; props: Props }) {
  const [renaming, setRenaming] = useState(false);
  const [adding, setAdding] = useState<PastPaperRole | null>(null);
  const [asking, setAsking] = useState(false);
  const files = paper.documents.length;
  return (
    <li className="doc past-paper" data-past-paper={paper.id}>
      <div className="doc__body">
        {renaming ? (
          <input
            aria-label={`Name for ${paper.name}`}
            defaultValue={paper.name}
            maxLength={60}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Escape") setRenaming(false);
              if (e.key === "Enter" && e.currentTarget.value.trim()) {
                e.preventDefault();
                void props.onRename(paper.id, e.currentTarget.value.trim());
                setRenaming(false);
              }
            }}
          />
        ) : (
          <span className="doc__name">{paper.name}</span>
        )}
        <span className="past-paper__chips">
          {chips(paper).map((c) => (
            <span key={c.role} className="past-paper__chip">
              {c.label}
              <Menu label={`More for ${c.label} of ${paper.name}`}>
                {c.docs.map((d) => (
                  <div key={d.id} className="row-menu__group">
                    {c.docs.length > 1 && <span className="row-menu__head">{d.filename}</span>}
                    <FileActions doc={d} papers={props.listing.papers} props={props} />
                  </div>
                ))}
              </Menu>
            </span>
          ))}
          {!hasScheme(paper) && (
            <span className="past-paper__missing">
              No mark scheme
              <button
                type="button"
                className="subject__link"
                onClick={() => setAdding("mark_scheme")}
              >
                Add
              </button>
            </span>
          )}
        </span>
        {adding && (
          <span className="past-paper__add">
            <select
              aria-label="What the file is"
              value={adding}
              onChange={(e) => setAdding(e.target.value as PastPaperRole)}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_WORD[r]}
                </option>
              ))}
            </select>
            <label className="button">
              Choose file
              <input
                type="file"
                accept="application/pdf"
                aria-label={`Add a file to ${paper.name}`}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void props.onAddFile(paper.id, adding, f);
                  setAdding(null);
                }}
              />
            </label>
            <button type="button" className="button button--quiet" onClick={() => setAdding(null)}>
              Cancel
            </button>
          </span>
        )}
        {asking && (
          <span className="past-paper__confirm">
            Delete {paper.name} and its {files} file{files === 1 ? "" : "s"}? They can't be brought
            back.
            <button
              type="button"
              className="button button--danger"
              onClick={() => void props.onDeletePaper(paper.id)}
            >
              Delete paper
            </button>
            <button type="button" className="button" onClick={() => setAsking(false)}>
              Keep it
            </button>
          </span>
        )}
      </div>
      <div className="doc__actions">
        <Menu label={`More for ${paper.name}`}>
          <button type="button" onClick={() => setAdding("question_paper")}>
            Add a file
          </button>
          <button type="button" onClick={() => setRenaming(true)}>
            Rename
          </button>
          <button type="button" onClick={() => setAsking(true)}>
            Delete paper
          </button>
        </Menu>
      </div>
    </li>
  );
}

export function PastPapers(props: Props) {
  const { listing, uploading, failures } = props;
  const sessions = bySession(listing.papers);
  const waiting = listing.unmatched.filter((d) => d.status === "uploaded" && d.described_at);
  const open = papersWithoutScheme(listing);
  if (sessions.length === 0 && waiting.length === 0 && uploading.length === 0 && !failures.length)
    return null;
  return (
    <div className="past-papers" aria-label="Past papers" role="group">
      <h3 className="past-papers__title">Past papers</h3>
      {(uploading.length > 0 || failures.length > 0) && (
        <>
          <h4 className="past-papers__session">Uploading</h4>
          <ul className="doc-list">
            {uploading}
            {failures.map((f) => (
              <li key={f.name} className="doc">
                <div className="doc__body">
                  <span className="doc__name">{f.name}</span>
                  <span className="doc__meta doc__meta--danger" role="alert">
                    {f.message}
                  </span>
                </div>
                <div className="doc__actions">
                  <button type="button" className="button" onClick={f.retry}>
                    Retry
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
      {sessions.map((s) => (
        <div key={s.label}>
          <h4 className="past-papers__session">{s.label}</h4>
          <ul className="doc-list">
            {s.papers.map((p) => (
              <PaperRow key={p.id} paper={p} props={props} />
            ))}
          </ul>
        </div>
      ))}
      {waiting.length > 0 && (
        <>
          <h4 className="past-papers__session">Not matched</h4>
          <ul className="doc-list">
            {waiting.map((d) => (
              <li key={d.id} className="doc past-paper">
                <div className="doc__body">
                  <span className="doc__name">{d.filename}</span>
                  <span className="doc__meta">
                    {ROLE_WORD[d.role ?? "question_paper"]} · {pp(d)}
                  </span>
                  <label className="past-paper__which">
                    Which paper is this for?
                    <select
                      aria-label={`Which paper is ${d.filename} for?`}
                      defaultValue=""
                      onChange={(e) => e.target.value && void props.onMove(d.id, e.target.value)}
                    >
                      <option value="" disabled>
                        Choose a paper
                      </option>
                      {open.map((p) => (
                        <option key={p.id} value={p.id}>
                          {paperOption(p)}
                        </option>
                      ))}
                      <option value="new">A paper of its own</option>
                    </select>
                  </label>
                </div>
                <div className="doc__actions">
                  <Menu label={`More for ${d.filename}`}>
                    <FileActions doc={d} papers={listing.papers} props={props} />
                  </Menu>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
